import { Link } from '@tanstack/react-router';
import { Code2, BrainCircuit, BarChart3 } from 'lucide-react';

import type { HomeBatchProgress } from '@/lib/api/home';

/** Card accents cycle in the design's order: orange → violet → sky. */
const ACCENTS = [
  { tile: '#fff4f0', fg: '#ff5a1f' },
  { tile: '#f5f3ff', fg: '#8b5cf6' },
  { tile: '#f0f9ff', fg: '#0ea5e9' },
];

const BATCH_ICONS = [Code2, BrainCircuit, BarChart3];

function fmtNext(iso?: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);
  const time = d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
  if (d.toDateString() === today.toDateString()) return `Next: Today, ${time}`;
  if (d.toDateString() === tomorrow.toDateString()) return `Next: Tomorrow, ${time}`;
  return `Next: ${d.toLocaleDateString('en-US', { weekday: 'short' })}, ${time}`;
}

export function BatchesPanel({ batches }: { batches: HomeBatchProgress[] }) {
  return (
    <section aria-label="My batches">
      <div className="flex items-center justify-between">
        <h3 className="text-[16px] font-semibold leading-[24px] text-[#111827]">My Batches</h3>
        <Link
          to="/courses"
          className="text-[13px] font-medium leading-[19.5px] text-[#ff5a1f] transition-opacity hover:opacity-75"
        >
          View all
        </Link>
      </div>

      {batches.length === 0 ? (
        <p className="py-6 text-center text-[13px] text-[#9ca3af]">No batches yet</p>
      ) : (
        <div className="grid grid-cols-1 gap-4 pt-4 sm:grid-cols-2 xl:grid-cols-3">
          {batches.map((b, idx) => {
            const accent = ACCENTS[idx % ACCENTS.length];
            const Icon = BATCH_ICONS[idx % BATCH_ICONS.length];
            const pct = Math.round(b.progress_pct);
            const next = fmtNext(b.next_session_at);

            return (
              <Link
                key={b.id}
                to="/courses/$batchId"
                params={{ batchId: b.id }}
                className="flex items-start gap-[14px] rounded-[20px] border border-[#ececec] bg-white p-[21px] shadow-[0_1px_2px_rgba(0,0,0,0.04)] transition-shadow hover:shadow-[0_4px_12px_rgba(0,0,0,0.06)]"
              >
                <span
                  className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[12px]"
                  style={{ backgroundColor: accent.tile }}
                >
                  <Icon className="h-5 w-5" style={{ color: accent.fg }} />
                </span>

                <div className="min-w-0 flex-1">
                  <p className="truncate text-[14px] font-semibold leading-[21px] text-[#111827]">{b.name}</p>

                  <div className="mt-1 h-[5px] w-full overflow-hidden rounded-[10px] bg-[#f3f4f6]">
                    <div
                      className="h-full rounded-[10px] transition-all"
                      style={{ width: `${pct}%`, backgroundColor: accent.fg }}
                    />
                  </div>
                  <p className="mt-1 text-[11px] leading-[16.5px] text-[#9ca3af]">{pct}% Complete</p>

                  <div className="mt-2 flex items-center gap-[6px] rounded-[8px] bg-[#fafafa] px-2 py-[5px]">
                    <span
                      className="h-[5px] w-[5px] shrink-0 rounded-[2.5px]"
                      style={{ backgroundColor: accent.fg }}
                    />
                    <span className="truncate text-[11px] leading-[16.5px] text-[#6b7280]">
                      {next ?? (b.trainer_name || 'No session scheduled')}
                    </span>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </section>
  );
}
